/**
 * Language selector — toggle panels, submit localization forms, sync active locale.
 */
(function () {
  const ROOT_SELECTOR = '[data-judy-language-selector]';
  const UTILITY_SELECTOR = '.editorial-homepage__utility--language';
  const MOBILE_MQ = window.matchMedia('(max-width: 989px)');
  const LOCALE_STORAGE_KEY = 'judy:selected-locale';
  const PORTAL_CLASS = 'judy-language-selector__panel--portaled';

  /** @type {WeakMap<HTMLElement, { panel: HTMLElement, placeholder: Comment, portaled: boolean }>} */
  const portalRegistry = new WeakMap();

  /**
   * @param {string | null | undefined} code
   * @returns {string}
   */
  function normalizeLocale(code) {
    return (code || '').toLowerCase().trim();
  }

  /**
   * @param {string | null | undefined} a
   * @param {string | null | undefined} b
   * @returns {boolean}
   */
  function localesMatch(a, b) {
    const left = normalizeLocale(a);
    const right = normalizeLocale(b);

    if (!left || !right) return false;
    if (left === right) return true;

    return left.split('-')[0] === right.split('-')[0];
  }

  /**
   * @returns {string}
   */
  function readServerLocale() {
    const jsonEl = document.getElementById('judy-shop-locale');
    if (jsonEl) {
      try {
        const parsed = JSON.parse(jsonEl.textContent || '{}');
        if (parsed && parsed.iso_code) return String(parsed.iso_code);
      } catch (_) {
        /* ignore */
      }
    }

    const htmlLocale = document.documentElement.getAttribute('data-shop-locale');
    if (htmlLocale) return htmlLocale;

    return document.documentElement.lang || '';
  }

  /**
   * @returns {string}
   */
  function readStoredLocale() {
    try {
      return sessionStorage.getItem(LOCALE_STORAGE_KEY) || '';
    } catch (_) {
      return '';
    }
  }

  /**
   * Reconcile stored preference with server-rendered locale signals.
   * @returns {string}
   */
  function resolveCurrentLocale() {
    const serverLocale = readServerLocale();
    const storedLocale = readStoredLocale();

    if (!storedLocale) {
      return serverLocale;
    }

    if (!serverLocale) {
      return storedLocale;
    }

    if (localesMatch(storedLocale, serverLocale)) {
      return storedLocale;
    }

    // Keep explicit user choice when another page renders a stale html[lang].
    return storedLocale;
  }

  /**
   * Sync sessionStorage with authoritative page locale without clobbering a newer user choice.
   */
  function syncStoredLocaleWithPage() {
    const serverLocale = readServerLocale();
    const storedLocale = readStoredLocale();

    if (!serverLocale) return;

    if (!storedLocale || localesMatch(storedLocale, serverLocale)) {
      rememberLocale(serverLocale);
    }
  }

  /**
   * @param {string} locale
   */
  function rememberLocale(locale) {
    if (!locale) return;

    try {
      sessionStorage.setItem(LOCALE_STORAGE_KEY, locale);
    } catch (_) {
      /* ignore */
    }
  }

  /**
   * @param {HTMLElement} scope
   */
  function applyCurrentLocaleToScope(scope) {
    const locale = resolveCurrentLocale();
    if (!locale) return;

    scope.setAttribute('data-current-language', locale);

    const nested = scope.querySelector('[data-judy-language-panel][data-current-language]');
    if (nested instanceof HTMLElement) {
      nested.setAttribute('data-current-language', locale);
    }
  }

  /**
   * @param {HTMLSelectElement | null} select
   * @param {string} targetLocale
   * @returns {string}
   */
  function findMatchingSelectValue(select, targetLocale) {
    if (!select) return '';

    const target = normalizeLocale(targetLocale);

    for (const option of select.options) {
      if (normalizeLocale(option.value) === target) {
        return option.value;
      }
    }

    for (const option of select.options) {
      if (localesMatch(option.value, targetLocale)) {
        return option.value;
      }
    }

    return select.value;
  }

  /**
   * @param {HTMLElement} scope
   * @returns {string}
   */
  function getScopeLocale(scope) {
    applyCurrentLocaleToScope(scope);

    const fromScope = scope.getAttribute('data-current-language');
    if (fromScope) return fromScope;

    const nested = scope.querySelector('[data-current-language]');
    if (nested instanceof HTMLElement) {
      return nested.getAttribute('data-current-language') || '';
    }

    return resolveCurrentLocale();
  }

  /**
   * @param {HTMLElement} scope
   */
  function syncActiveLanguageState(scope) {
    const currentLocale = getScopeLocale(scope);
    const select = scope.querySelector('select[name="language_code"]');
    const options = scope.querySelectorAll('[data-judy-language-option]');
    const matchedValue = findMatchingSelectValue(select, currentLocale);

    if (select && matchedValue) {
      select.value = matchedValue;
    }

    options.forEach((optionButton) => {
      const languageCode = optionButton.getAttribute('data-language-code') || '';
      const isActive =
        localesMatch(languageCode, currentLocale) ||
        (matchedValue !== '' && languageCode === matchedValue);

      optionButton.classList.toggle('is-active', isActive);
      optionButton.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
  }

  /**
   * @param {HTMLElement} root
   * @returns {HTMLElement | null}
   */
  function getPanel(root) {
    const entry = portalRegistry.get(root);
    if (entry?.portaled) {
      return entry.panel;
    }

    const panel = root.querySelector('[data-judy-language-panel]');
    return panel instanceof HTMLElement ? panel : null;
  }

  /**
   * @param {HTMLElement} root
   * @param {HTMLElement} panel
   * @param {HTMLElement} toggle
   */
  function positionPortaledPanel(root, panel, toggle) {
    const rect = toggle.getBoundingClientRect();
    const panelWidth = panel.offsetWidth || 160;

    panel.style.position = 'fixed';
    panel.style.top = Math.round(rect.bottom + 6) + 'px';
    panel.style.left = 'auto';
    panel.style.right = Math.max(8, Math.round(window.innerWidth - rect.right)) + 'px';
    panel.style.minWidth = Math.max(panelWidth, 144) + 'px';
    panel.style.zIndex = '10050';
  }

  /**
   * @param {HTMLElement} root
   */
  function portalPanel(root) {
    if (!MOBILE_MQ.matches) return;

    let entry = portalRegistry.get(root);
    const panel = root.querySelector('[data-judy-language-panel]');
    const toggle = root.querySelector('[data-judy-language-toggle]');

    if (!(panel instanceof HTMLElement) || !(toggle instanceof HTMLElement)) return;

    if (!entry) {
      entry = {
        panel: panel,
        placeholder: document.createComment('judy-language-panel-anchor'),
        portaled: false,
      };
      portalRegistry.set(root, entry);
    }

    if (entry.portaled) {
      positionPortaledPanel(root, entry.panel, toggle);
      return;
    }

    panel.parentNode?.insertBefore(entry.placeholder, panel);
    document.body.appendChild(panel);
    panel.classList.add(PORTAL_CLASS);
    entry.portaled = true;
    positionPortaledPanel(root, panel, toggle);
  }

  /**
   * @param {HTMLElement} root
   */
  function restorePortaledPanel(root) {
    const entry = portalRegistry.get(root);
    if (!entry || !entry.portaled) return;

    const { panel, placeholder } = entry;

    if (placeholder.parentNode) {
      placeholder.parentNode.insertBefore(panel, placeholder.nextSibling);
    }

    placeholder.remove();
    panel.classList.remove(PORTAL_CLASS);
    panel.style.position = '';
    panel.style.top = '';
    panel.style.left = '';
    panel.style.right = '';
    panel.style.minWidth = '';
    panel.style.zIndex = '';
    entry.portaled = false;
  }

  /**
   * @param {HTMLElement} root
   * @param {HTMLElement} panel
   * @returns {boolean}
   */
  function isPanelOpen(root, panel) {
    return root.classList.contains('is-open') && panel.hidden === false;
  }

  /**
   * @param {HTMLElement} root
   */
  function closePanel(root) {
    const toggle = root.querySelector('[data-judy-language-toggle]');
    const panel = getPanel(root);

    if (!toggle || !panel) return;

    panel.hidden = true;
    toggle.setAttribute('aria-expanded', 'false');
    root.classList.remove('is-open');
    restorePortaledPanel(root);
  }

  /**
   * @param {HTMLElement} root
   */
  function openPanel(root) {
    const toggle = root.querySelector('[data-judy-language-toggle]');
    const panel = root.querySelector('[data-judy-language-panel]');

    if (!toggle || !panel) return;

    document.querySelectorAll(ROOT_SELECTOR).forEach((other) => {
      if (other !== root) closePanel(other);
    });

    syncActiveLanguageState(root);
    portalPanel(root);

    const activePanel = getPanel(root) || panel;
    activePanel.hidden = false;
    toggle.setAttribute('aria-expanded', 'true');
    root.classList.add('is-open');

    const portaledToggle = root.querySelector('[data-judy-language-toggle]');
    if (portaledToggle instanceof HTMLElement && MOBILE_MQ.matches) {
      positionPortaledPanel(root, activePanel, portaledToggle);
    }
  }

  /**
   * @param {HTMLElement | null} root
   * @param {EventTarget | null} target
   * @returns {boolean}
   */
  function rootInteractionContains(root, target) {
    if (!(root instanceof HTMLElement) || !(target instanceof Node)) {
      return false;
    }

    if (root.contains(target)) return true;

    const panel = getPanel(root);
    return Boolean(panel && panel.contains(target));
  }

  /**
   * @param {HTMLFormElement | null} form
   * @param {HTMLSelectElement | null} select
   * @param {string} languageCode
   * @param {HTMLElement} scope
   */
  function submitLanguage(form, select, languageCode, scope) {
    if (!form || !select || !languageCode) return;

    const matchedValue = findMatchingSelectValue(select, languageCode);
    const currentValue = findMatchingSelectValue(select, getScopeLocale(scope));

    if (matchedValue && currentValue && matchedValue === currentValue) {
      closePanel(scope.closest(ROOT_SELECTOR) || scope);
      return;
    }

    if (matchedValue) {
      select.value = matchedValue;
      rememberLocale(matchedValue);
    } else {
      select.value = languageCode;
      rememberLocale(languageCode);
    }

    closePanel(scope.closest(ROOT_SELECTOR) || scope);
    form.submit();
  }

  /**
   * @param {HTMLElement} scope
   */
  function initLanguageOptions(scope) {
    const form = scope.querySelector('form');
    const select = scope.querySelector('select[name="language_code"]');
    const options = scope.querySelectorAll('[data-judy-language-option]');

    if (!form || !select || options.length === 0) return;

    applyCurrentLocaleToScope(scope);
    syncActiveLanguageState(scope);

    options.forEach((optionButton) => {
      if (optionButton.dataset.judyLanguageOptionInit === 'true') return;
      optionButton.dataset.judyLanguageOptionInit = 'true';

      optionButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        const languageCode = optionButton.getAttribute('data-language-code');
        submitLanguage(form, select, languageCode || '', scope);
      });
    });
  }

  /**
   * @param {HTMLElement} root
   */
  function initLanguageSelector(root) {
    if (root.dataset.judyLanguageInit === 'true') {
      applyCurrentLocaleToScope(root);
      syncActiveLanguageState(root);
      return;
    }

    root.dataset.judyLanguageInit = 'true';

    initLanguageOptions(root);

    const toggle = root.querySelector('[data-judy-language-toggle]');
    const panel = root.querySelector('[data-judy-language-panel]');

    if (!toggle || !panel) return;

    panel.hidden = true;
    root.classList.remove('is-open');
    toggle.setAttribute('aria-expanded', 'false');

    toggle.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();

      const activePanel = getPanel(root) || panel;

      if (isPanelOpen(root, activePanel)) {
        closePanel(root);
        return;
      }

      openPanel(root);
    });
  }

  /**
   * @param {HTMLElement} utility
   */
  function initDesktopLanguageUtility(utility) {
    if (utility.dataset.judyLanguageUtilityInit !== 'true') {
      utility.dataset.judyLanguageUtilityInit = 'true';
    }

    applyCurrentLocaleToScope(utility);
    initLanguageOptions(utility);
  }

  function closeAllPanels() {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      closePanel(root);
    });
  }

  function initAll() {
    syncStoredLocaleWithPage();

    document.querySelectorAll(ROOT_SELECTOR).forEach(initLanguageSelector);
    document.querySelectorAll(UTILITY_SELECTOR).forEach(initDesktopLanguageUtility);
    refreshLanguageState();
  }

  function refreshLanguageState() {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      applyCurrentLocaleToScope(root);
      syncActiveLanguageState(root);
    });
    document.querySelectorAll(UTILITY_SELECTOR).forEach((utility) => {
      applyCurrentLocaleToScope(utility);
      syncActiveLanguageState(utility);
    });
  }

  document.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) return;

      if (event.target.closest('[data-judy-language-option]')) {
        return;
      }

      document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
        if (!rootInteractionContains(root, event.target)) {
          closePanel(root);
        }
      });
    },
    true
  );

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    closeAllPanels();
  });

  document.addEventListener('editorial:mobile-hub-toggle', (event) => {
    if (!(event instanceof CustomEvent)) return;
    if (event.detail && event.detail.open === false) {
      closeAllPanels();
    }
  });

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      window.location.reload();
      return;
    }

    syncStoredLocaleWithPage();
    refreshLanguageState();
  });

  window.addEventListener('pagereveal', () => {
    syncStoredLocaleWithPage();
    refreshLanguageState();
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    refreshLanguageState();
  });

  window.addEventListener('resize', () => {
    if (!MOBILE_MQ.matches) {
      document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
        restorePortaledPanel(root);
      });
      return;
    }

    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (!root.classList.contains('is-open')) return;

      const toggle = root.querySelector('[data-judy-language-toggle]');
      const panel = getPanel(root);
      if (toggle instanceof HTMLElement && panel instanceof HTMLElement) {
        positionPortaledPanel(root, panel, toggle);
      }
    });
  });

  if (typeof MOBILE_MQ.addEventListener === 'function') {
    MOBILE_MQ.addEventListener('change', () => {
      closeAllPanels();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('judy:language-sync', () => {
    refreshLanguageState();
  });

  document.addEventListener('shopify:section:load', initAll);
})();
